import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';

export default function GenericModal({ isOpen, onClose, children, draggable = false, dragHandleSelector, hideTopRightClose = false, fitToContent = false, maxWidthClass = 'max-w-4xl' }) {
  const [isDragging, setIsDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const startMouseRef = useRef({ x: 0, y: 0 });
  const startOffsetRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!draggable) return;

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const dx = e.clientX - startMouseRef.current.x;
      const dy = e.clientY - startMouseRef.current.y;
      setOffset({ x: startOffsetRef.current.x + dx, y: startOffsetRef.current.y + dy });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggable, isDragging]);

  // Reset position when opening
  useEffect(() => {
    if (isOpen) {
      setOffset({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [isOpen]);

  const beginDrag = (e) => {
    if (!draggable) return;
    if (e.button !== 0) return; // left click only
    // If a drag handle is specified, only start drag when initiating from the handle
    if (dragHandleSelector) {
      const handles = Array.from(e.currentTarget.querySelectorAll(dragHandleSelector));
      if (handles.length === 0) return;
      const isFromHandle = handles.some((h) => h.contains(e.target));
      if (!isFromHandle) return;
    }
    setIsDragging(true);
    startMouseRef.current = { x: e.clientX, y: e.clientY };
    startOffsetRef.current = { ...offset };
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-40" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
        <DialogPanel
          className={`bg-gray-800 rounded-lg shadow-xl ${fitToContent ? 'w-auto' : `w-full ${maxWidthClass}`} p-6 relative overflow-auto break-words whitespace-normal`}
          style={{
            maxHeight: '90vh',
            maxWidth: fitToContent ? '90vw' : undefined,
            transform: draggable ? `translate(${offset.x}px, ${offset.y}px)` : undefined
          }}
          onMouseDown={beginDrag}
        >
          {!hideTopRightClose && (
            <button
              onClick={onClose}
              className="absolute top-2 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold focus:outline-none"
              aria-label="Close"
            >
              &times;
            </button>
          )}
          {children}
        </DialogPanel>
      </div>
    </Dialog>
  );
} 